/* iter543 — Advisory Board COI Declaration management panel
 *
 * Admin-only UI that lists Clinical Advisory Partners and lets the admin
 * record their COI declaration (one row per category: disclosed yes/no +
 * free-text details). Backed by:
 *   GET  /api/ambassadors/admin/advisory-partners
 *   GET  /api/ambassadors/admin/{id}/coi-declaration
 *   POST /api/ambassadors/admin/{id}/coi-declaration
 *
 * History (`coi_history`) is preserved on every save so the Board has a
 * full audit trail.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ShieldAlert, Check, AlertTriangle, Loader2, Save, RefreshCw } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";
import { useTheme } from "@/theme/ThemeContext";
import { useAuth } from "@/App";

export default function AdvisoryCOIPanel() {
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const { API } = useAuth();
  const bg = language === "bg";

  const [partners, setPartners] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);    // { id, full_name, declaration, history, categories }
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [saving, setSaving] = useState(false);

  // Edit-state buffer for declarations and attestation
  const [editDecl, setEditDecl] = useState({});  // { [catId]: { disclosed, details } }
  const [noConflicts, setNoConflicts] = useState(false);
  const [adminNotes, setAdminNotes] = useState("");

  const loadList = async () => {
    setLoadingList(true);
    try {
      const { data } = await axios.get(`${API}/ambassadors/admin/advisory-partners`);
      setPartners(data?.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || (bg ? "Грешка при зареждане" : "Failed to load"));
    } finally {
      setLoadingList(false);
    }
  };

  const loadDetail = async (id) => {
    setLoadingDetail(true);
    try {
      const { data } = await axios.get(`${API}/ambassadors/admin/${id}/coi-declaration`);
      setDetail(data);
      const decl = data?.declaration?.declarations || {};
      const seeded = {};
      (data?.categories || []).forEach((c) => {
        seeded[c.id] = {
          disclosed: !!decl[c.id]?.disclosed,
          details: decl[c.id]?.details || "",
        };
      });
      setEditDecl(seeded);
      setNoConflicts(!!data?.declaration?.no_conflicts_attestation);
      setAdminNotes(data?.declaration?.admin_notes || "");
    } catch (e) {
      toast.error(e?.response?.data?.detail || (bg ? "Грешка" : "Error"));
      setDetail(null);
    } finally {
      setLoadingDetail(false);
    }
  };

  useEffect(() => {
    // iter543 — initial fetch on mount; further refreshes go through loadList.
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/ambassadors/admin/advisory-partners`);
        if (!cancelled) setPartners(data?.items || []);
      } catch (e) {
        if (!cancelled) toast.error(e?.response?.data?.detail || (bg ? "Грешка при зареждане" : "Failed to load"));
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const onSave = async () => {
    if (!selectedId) return;
    // Sanity: cannot attest no-conflicts if any disclosed
    if (noConflicts && Object.values(editDecl).some((d) => d.disclosed)) {
      toast.error(bg
        ? "Не можеш да маркираш 'без конфликти' докато има disclosed категории"
        : "Cannot attest 'no conflicts' while a category is disclosed"
      );
      return;
    }
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/ambassadors/admin/${selectedId}/coi-declaration`, {
        declarations: editDecl,
        no_conflicts_attestation: noConflicts,
        admin_notes: adminNotes || null,
      });
      toast.success(bg ? "COI декларация записана" : "COI declaration saved");
      // refresh list to update counts
      await loadList();
      // refresh detail so history pushed
      await loadDetail(selectedId);
      return data;
    } catch (e) {
      toast.error(e?.response?.data?.detail || (bg ? "Грешка при запис" : "Save error"));
    } finally {
      setSaving(false);
    }
  };

  const containerCls = isDark
    ? "bg-[hsl(223,47%,11%)] border-[hsl(223,47%,15%)]"
    : "bg-white border-slate-200";
  const inputCls = isDark
    ? "bg-[hsl(223,47%,9%)] border-[hsl(223,47%,18%)] text-slate-100"
    : "bg-white border-slate-200 text-slate-800";

  return (
    <div className="space-y-6" data-testid="advisory-coi-panel">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center"
            style={{ background: "rgba(184,134,11,0.15)" }}
          >
            <ShieldAlert className="w-5 h-5" style={{ color: "#b8860b" }} />
          </div>
          <div>
            <h2 className={`text-base font-bold ${isDark ? "text-white" : "text-slate-900"}`}>
              {bg ? "Advisory Board — COI декларации" : "Advisory Board — COI declarations"}
            </h2>
            <p className={`text-xs ${isDark ? "text-slate-500" : "text-slate-500"}`}>
              {bg
                ? "Conflict-of-Interest управление за Clinical Advisory Partners. Всеки запис се audit-ва (coi_history)."
                : "Conflict-of-Interest management for Clinical Advisory Partners. Every save is audited (coi_history)."}
            </p>
          </div>
        </div>
        <button
          onClick={loadList}
          data-testid="coi-refresh-btn"
          className={`text-xs inline-flex items-center gap-1.5 px-3 h-8 rounded-lg border transition-colors ${
            isDark ? "border-slate-700 hover:bg-slate-800 text-slate-200" : "border-slate-200 hover:bg-slate-50 text-slate-700"
          }`}
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loadingList ? "animate-spin" : ""}`} />
          {bg ? "Обнови" : "Refresh"}
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-4">
        {/* Partner list */}
        <div className={`rounded-2xl border ${containerCls} max-h-[500px] overflow-y-auto`} data-testid="coi-partner-list">
          {partners.length === 0 && !loadingList && (
            <p className={`text-xs italic p-4 ${isDark ? "text-slate-500" : "text-slate-400"}`}>
              {bg ? "Все още няма Advisory Partners. Поканете чрез admin invite endpoint." : "No Advisory Partners yet. Onboard via the admin invite endpoint."}
            </p>
          )}
          {loadingList && (
            <p className={`text-xs italic p-4 ${isDark ? "text-slate-500" : "text-slate-400"}`}>
              <Loader2 className="w-3 h-3 inline animate-spin mr-1" />
              {bg ? "Зареждане…" : "Loading…"}
            </p>
          )}
          {partners.map((p) => {
            const isActive = p.id === selectedId;
            const received = p.coi_declaration_received;
            const noConfFiled = p.coi_summary?.no_conflicts_attestation;
            const disclosedN = p.coi_summary?.disclosed_count || 0;
            return (
              <button
                key={p.id}
                onClick={() => { setSelectedId(p.id); loadDetail(p.id); }}
                data-testid={`coi-partner-${p.id}`}
                className={`w-full text-left p-3 border-b last:border-b-0 transition-colors ${
                  isDark ? "border-slate-800 hover:bg-slate-800/40" : "border-slate-100 hover:bg-slate-50"
                } ${isActive ? (isDark ? "bg-slate-800/60" : "bg-amber-50/60") : ""}`}
              >
                <div className="flex items-center justify-between mb-1">
                  <span className={`text-sm font-semibold ${isDark ? "text-white" : "text-slate-900"}`}>
                    {p.full_name}
                  </span>
                  {received ? (
                    noConfFiled ? (
                      <span className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-widest text-emerald-700 bg-emerald-100 px-1.5 py-0.5 rounded">
                        <Check className="w-2.5 h-2.5" /> {bg ? "Чист" : "Clean"}
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-widest text-amber-800 bg-amber-100 px-1.5 py-0.5 rounded">
                        <AlertTriangle className="w-2.5 h-2.5" /> {disclosedN}
                      </span>
                    )
                  ) : (
                    <span className="text-[9px] font-bold uppercase tracking-widest text-red-700 bg-red-100 px-1.5 py-0.5 rounded">
                      {bg ? "Липсва" : "Missing"}
                    </span>
                  )}
                </div>
                <div className={`text-[10px] ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                  {p.profession || "—"} · {p.institution || "—"}
                </div>
                <div className={`text-[10px] mt-0.5 ${isDark ? "text-slate-500" : "text-slate-400"}`}>
                  {p.email}
                </div>
              </button>
            );
          })}
        </div>

        {/* Detail / edit form */}
        <div className={`rounded-2xl border p-5 ${containerCls}`} data-testid="coi-detail-panel">
          {!selectedId && (
            <p className={`text-sm italic ${isDark ? "text-slate-500" : "text-slate-400"}`}>
              {bg ? "Избери партньор отляво за да редактираш COI." : "Select a partner on the left to edit COI."}
            </p>
          )}
          {selectedId && loadingDetail && (
            <p className={`text-sm italic ${isDark ? "text-slate-500" : "text-slate-400"}`}>
              <Loader2 className="w-3 h-3 inline animate-spin mr-1" />
              {bg ? "Зареждане…" : "Loading…"}
            </p>
          )}
          {selectedId && detail && !loadingDetail && (
            <>
              <div className="mb-5 flex flex-wrap items-baseline justify-between gap-2">
                <div>
                  <h3 className={`text-base font-bold ${isDark ? "text-white" : "text-slate-900"}`}>
                    {detail.full_name}
                  </h3>
                  {detail.declaration?.submitted_at && (
                    <p className={`text-[11px] ${isDark ? "text-slate-500" : "text-slate-500"}`}>
                      {bg ? "Последен запис" : "Last recorded"}:{" "}
                      {new Date(detail.declaration.submitted_at).toLocaleString()} · {bg ? "от" : "by"}{" "}
                      {detail.declaration.recorded_by}
                    </p>
                  )}
                </div>
                {detail.history?.length > 0 && (
                  <span className={`text-[10px] font-mono px-2 py-0.5 rounded ${isDark ? "bg-slate-800 text-slate-300" : "bg-slate-100 text-slate-600"}`}>
                    {detail.history.length} {bg ? "версии в audit" : "audit entries"}
                  </span>
                )}
              </div>

              {/* No-conflicts attestation */}
              <label className={`flex items-start gap-2 p-3 rounded-lg border mb-5 cursor-pointer ${
                noConflicts
                  ? (isDark ? "bg-emerald-500/10 border-emerald-500/40" : "bg-emerald-50 border-emerald-200")
                  : (isDark ? "border-slate-700" : "border-slate-200")
              }`}>
                <input
                  type="checkbox"
                  checked={noConflicts}
                  onChange={(e) => setNoConflicts(e.target.checked)}
                  data-testid="coi-no-conflicts-attestation"
                  className="mt-0.5"
                />
                <span className={`text-xs ${isDark ? "text-slate-200" : "text-slate-700"}`}>
                  <strong>{bg ? "Декларация 'без конфликти на интереси'" : "'No conflicts of interest' attestation"}</strong>
                  <br />
                  <span className={isDark ? "text-slate-400" : "text-slate-500"}>
                    {bg
                      ? "Партньорът декларира, че няма material COI в нито една от 8-те категории."
                      : "The partner attests that there are no material COIs in any of the 8 categories."}
                  </span>
                </span>
              </label>

              {/* Category rows */}
              <div className="space-y-2 mb-5">
                {(detail.categories || []).map((cat) => {
                  const row = editDecl[cat.id] || { disclosed: false, details: "" };
                  return (
                    <div
                      key={cat.id}
                      data-testid={`coi-row-${cat.id}`}
                      className={`p-3 rounded-lg border transition-colors ${
                        row.disclosed
                          ? (isDark ? "bg-amber-500/10 border-amber-500/40" : "bg-amber-50/60 border-amber-200")
                          : (isDark ? "bg-[hsl(223,47%,9%)] border-slate-800" : "border-slate-200")
                      }`}
                    >
                      <label className="flex items-center gap-2 mb-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={row.disclosed}
                          onChange={(e) => {
                            const cur = editDecl[cat.id] || { disclosed: false, details: "" };
                            setEditDecl({ ...editDecl, [cat.id]: { ...cur, disclosed: e.target.checked } });
                          }}
                          data-testid={`coi-toggle-${cat.id}`}
                          disabled={noConflicts}
                        />
                        <span className={`text-sm font-medium ${isDark ? "text-slate-100" : "text-slate-800"}`}>
                          {bg ? cat.label_bg : cat.label_en}
                        </span>
                      </label>
                      {row.disclosed && (
                        <textarea
                          rows={2}
                          value={row.details}
                          onChange={(e) => {
                            const cur = editDecl[cat.id] || { disclosed: false, details: "" };
                            setEditDecl({ ...editDecl, [cat.id]: { ...cur, details: e.target.value } });
                          }}
                          placeholder={bg ? "Детайли (журнали, компании, дати, суми…)" : "Details (journals, companies, dates, amounts…)"}
                          data-testid={`coi-details-${cat.id}`}
                          className={`w-full mt-1 text-xs rounded-md p-2 border ${inputCls}`}
                        />
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Admin notes */}
              <div className="mb-5">
                <label className={`block text-[11px] uppercase tracking-widest font-bold mb-1.5 ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                  {bg ? "Admin бележки (internal)" : "Admin notes (internal)"}
                </label>
                <textarea
                  rows={3}
                  value={adminNotes}
                  onChange={(e) => setAdminNotes(e.target.value)}
                  placeholder={bg ? "Linкове към PDF, source email-и, escalation бележки…" : "Links to PDF, source emails, escalation notes…"}
                  data-testid="coi-admin-notes"
                  className={`w-full text-xs rounded-md p-2 border ${inputCls}`}
                />
              </div>

              <div className="flex items-center justify-end gap-2">
                <button
                  onClick={onSave}
                  disabled={saving}
                  data-testid="coi-save-btn"
                  className="inline-flex items-center gap-1.5 px-4 h-10 rounded-lg text-sm font-semibold text-white disabled:opacity-60"
                  style={{ background: "#b8860b" }}
                >
                  {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                  {bg ? "Запиши" : "Save"}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
